import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { AuthContext } from "~/hooks/use-auth";
import { api } from "~/lib/api";
import { supabase } from "~/lib/supabase";
import { useAuthStore } from "~/stores/use-auth-store";
import { useDeviceStore } from "~/stores/use-device-store";
import { usePermissionStore } from "~/stores/use-permission-store";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { setSession, setSessionChecked } = useAuthStore();

  const { deviceId } = useDeviceStore();

  const { mutate: disconnectDevice } =
    api.device.disconnectDevice.useMutation();

  const queryClient = useQueryClient();

  const resetPermissions = usePermissionStore((state) => state.reset);

  const navigate = useNavigate();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setSessionChecked(true);
      if (!session) {
        localStorage.clear();
        navigate({ to: "/login" })
          .then(() => {
            resetPermissions();
          })
          .catch((e) => console.error(e));
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, resetPermissions, setSession, setSessionChecked]);

  // In case we want to manually trigger a signIn (instead of using Auth UI)
  const signIn = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setSessionChecked(false);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error(error);
      return error;
    }

    setSession(data.session);

    return true;
  };

  const signOut = async () => {
    if (deviceId) {
      disconnectDevice(deviceId, {
        async onSuccess() {
          const { error } = await supabase.auth.signOut({
            scope: "local",
          });
          console.log("error: ", error);
          if (!error) {
            void queryClient.clear();

            setSession(null);
          }
          return { error };
        },
      });
    } else {
      const { error } = await supabase.auth.signOut({
        scope: "local",
      });
      console.log("error: ", error);
      if (!error) {
        setSession(null);
      }
      return { error };
    }
  };

  const context = {
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
};
